import { useState, useEffect, useCallback, useRef } from 'react';
import { useGoogleAuth } from '../contexts/GoogleAuthContext';
import { validateGoogleToken } from '../utils/googleCalendar';
import { refreshGoogleAccessToken } from '../utils/api';

interface UseAutoGoogleAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  lastChecked: Date | null;
  autoRefreshEnabled: boolean;
  enableAutoRefresh: () => void;
  disableAutoRefresh: () => void;
  forceTokenCheck: () => Promise<boolean>;
}

/**
 * Hook para gerenciar autenticação automática com Google Calendar
 * Inclui verificação periódica e renovação automática de tokens
 */
export const useAutoGoogleAuth = (): UseAutoGoogleAuthReturn => {
  const { 
    accessToken, 
    isAuthenticated, 
    isLoading, 
    error, 
    validateToken,
    reloadTokens 
  } = useGoogleAuth();
  
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [isChecking, setIsChecking] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastTokenRef = useRef<string | null>(null);

  // Verificar se o token mudou
  useEffect(() => {
    if (accessToken !== lastTokenRef.current) {
      lastTokenRef.current = accessToken;
      if (accessToken) {
        setLastChecked(new Date());
        setAuthError(null);
      }
    }
  }, [accessToken]);

  // Função para verificar e renovar token automaticamente
  const checkAndRefreshToken = useCallback(async (): Promise<boolean> => {
    if (!accessToken || isChecking) {
      return isAuthenticated;
    }

    setIsChecking(true);
    setAuthError(null);

    try {
      console.log("🔄 [AUTO_AUTH] Verificando token automaticamente...");
      
      // Verificar se o token ainda é válido
      const isValid = await validateGoogleToken(accessToken);
      
      if (isValid) {
        console.log("✅ [AUTO_AUTH] Token ainda válido");
        setLastChecked(new Date());
        return true;
      }

      console.log("⚠️ [AUTO_AUTH] Token inválido, tentando renovar...");
      
      // Tentar renovar usando o contexto
      const renewed = await validateToken();
      
      if (renewed) {
        console.log("✅ [AUTO_AUTH] Token renovado com sucesso");
        setLastChecked(new Date());
        setAuthError(null);
        return true;
      } else {
        console.warn("❌ [AUTO_AUTH] Falha ao renovar token");
        setAuthError("Token expirado e não foi possível renovar automaticamente");
        return false;
      }
    } catch (error) {
      console.error("❌ [AUTO_AUTH] Erro na verificação automática:", error);
      setAuthError("Erro na verificação automática do token");
      return false;
    } finally {
      setIsChecking(false);
    }
  }, [accessToken, isAuthenticated, isChecking, validateToken]);

  // Verificação periódica em background
  useEffect(() => {
    if (!autoRefreshEnabled || !isAuthenticated) {
      return;
    }

    // Verificar imediatamente se não foi verificado recentemente
    const shouldCheckNow = !lastChecked || 
      (Date.now() - lastChecked.getTime()) > 5 * 60 * 1000; // 5 minutos

    if (shouldCheckNow) {
      checkAndRefreshToken();
    }

    // Configurar verificação periódica a cada 10 minutos
    intervalRef.current = setInterval(async () => {
      if (autoRefreshEnabled && isAuthenticated) {
        console.log("⏰ [AUTO_AUTH] Verificação periódica iniciada");
        await checkAndRefreshToken();
      }
    }, 10 * 60 * 1000); // 10 minutos

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autoRefreshEnabled, isAuthenticated, lastChecked, checkAndRefreshToken]);

  // Verificação antes de operações críticas
  const forceTokenCheck = useCallback(async (): Promise<boolean> => {
    console.log("🔍 [AUTO_AUTH] Verificação forçada do token...");
    if (!accessToken) {
      await reloadTokens();
      return false;
    }
    return await checkAndRefreshToken();
  }, [accessToken, checkAndRefreshToken, reloadTokens]);

  // Uma tentativa de restaurar do backend se o contexto ainda não tem token.
  const triedRestoreRef = useRef(false);
  useEffect(() => {
    if (isLoading || accessToken || !autoRefreshEnabled) {
      if (accessToken) triedRestoreRef.current = false;
      return;
    }
    if (triedRestoreRef.current) return;
    triedRestoreRef.current = true;
    void reloadTokens();
  }, [isLoading, accessToken, autoRefreshEnabled, reloadTokens]);

  // Habilitar/desabilitar auto-refresh
  const enableAutoRefresh = useCallback(() => {
    setAutoRefreshEnabled(true);
    console.log("✅ [AUTO_AUTH] Auto-refresh habilitado");
  }, []);

  const disableAutoRefresh = useCallback(() => {
    setAutoRefreshEnabled(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    console.log("⏸️ [AUTO_AUTH] Auto-refresh desabilitado");
  }, []);

  // Limpar interval ao desmontar
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    isAuthenticated: isAuthenticated && !authError,
    isLoading: isLoading || isChecking,
    error: error || authError,
    lastChecked,
    autoRefreshEnabled,
    enableAutoRefresh,
    disableAutoRefresh,
    forceTokenCheck,
  };
};
